import ng from 'angular';

import { BillingCycleToContractPeriodeConst, ContractPeriodNumber } from '@/configuration';
import { AuthContextService } from '@/services';
import { getVatAmount } from '@/services/get-vat-amount';

export type CalculatePriceFilter = (
    netPriceOrObj: number | CalculatePriceNew,
    vatRate?: number,
    quantity?: number,
    type?: string,
    exchangeRatio?: number,
    ignoreCommercial?: boolean,
    productCode?: string,
    billingPeriod?: number|string
) => number;

interface CalculatePriceOld {
    netPrice: number;
    vatRate: number;
    exchangeRatio: number;
    billingPeriod: number;
    quantity: number;
    productCode: string;
}

interface CalculatePriceNew {
    grossPrice: number;
    quantity?: number;
}

type validEndDigits = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7;

export class CalculatePrice {
    public static $inject: string[] = [];

    public static Factory(): CalculatePriceFilter {
        const isCommercialCustomer = AuthContextService.account.isCommercialCustomer;

        return (
            netPrice,
            vatRate,
            quantity,
            type,
            exchangeRatio,
            ignoreCommercial = false,
            productCode,
            billingPeriod
        ) => {
            const quantityIsOne = (quant: number): boolean => {
                return [undefined, null].indexOf(quant) >= 0 || !ng.isNumber(quant);
            };

            if (typeof netPrice === 'object') {
                const grossPrice = netPrice.grossPrice;
                const grossQuantity = quantityIsOne(netPrice.quantity) ? 1 : netPrice.quantity;
                return CalculatePrice.calculatePrice({
                    grossPrice: grossPrice,
                    quantity: grossQuantity
                });
            }

            if ([undefined, null, -1].indexOf(netPrice) >= 0 || !ng.isNumber(netPrice)) {
                return netPrice;
            }

            if (quantityIsOne(quantity)) {
                quantity = 1;
            }

            if ([undefined, null, 'net', 'gross'].indexOf(type) < 0) {
                type = null;
            }

            // sets the vat rate depending on whether we are gross or net!
            if ([undefined, null].indexOf(vatRate) >= 0 || !ng.isNumber(vatRate)) {
                if ([undefined, null, 'gross'].indexOf(type) >= 0) {
                    vatRate = 1900;
                } else {
                    vatRate = 0;
                }
            }

            if ([undefined, null, 'net'].indexOf(type) >= 0 && (isCommercialCustomer || ignoreCommercial)) {
                vatRate = 0;
            }

            let billingPeriodNum: ContractPeriodNumber;

            if (typeof billingPeriod === 'string') { // also accept strings
                billingPeriodNum = BillingCycleToContractPeriodeConst[billingPeriod];
            }

            return CalculatePrice.calculatePrice({
                netPrice: netPrice,
                vatRate: vatRate,
                quantity: quantity,
                exchangeRatio: exchangeRatio,
                productCode: productCode,
                billingPeriod: billingPeriodNum
            });
        };
    }


    public static roundDigits(value: number, endDigits: validEndDigits): number {
        const roundedValue = CalculatePrice.backendRounding(Math.round(value * 10000000), 7 - endDigits);
        return Math.trunc(roundedValue) / Math.pow(10, endDigits);
    }

    private static backendRounding(mantissa: number, iterations: number): number {
        //  last <iterations> digits of will be cut later
        //  if last digits > 5 * 10^<iterations>
        //      add half of 10^iterations, which will
        //      be cut anyways but adds 1 to the last
        //      uncut digit

        if (iterations == 0) {
            return mantissa;
        }

        let divisor = 1;
        for (let i = 0; i < iterations; i++) {
            divisor *= 10;
        }

        let half = (divisor / 2);
        if (mantissa > 0) {
            if (mantissa % divisor >= half) {
                // round away from 0, i.e. towards \inf
                return (mantissa + half) / divisor;
            } else {
                // round towards 0, i.e. truncate
                return (mantissa) / divisor;
            }
        } else {
            if (Math.abs(mantissa) % divisor >= half) {
                // round away from 0, i.e. towards -\inf
                return (mantissa - half) / divisor;
            } else {
                // round towards 0, i.e. truncate
                return (mantissa) / divisor;
            }
        }
    }


    private static calculatePrice(params: CalculatePriceOld | CalculatePriceNew): number {
        const oldPar = params as CalculatePriceOld;
        const newPar = params as CalculatePriceNew;

        let totalPrice: number;

        if (newPar.grossPrice) {
            totalPrice = newPar.grossPrice * newPar.quantity / 10000.0;
        } else if (oldPar.netPrice && !isNaN(oldPar.vatRate)) {
            oldPar.exchangeRatio = this._getExchangeRatio(oldPar.exchangeRatio);

            oldPar.netPrice = oldPar.netPrice / oldPar.exchangeRatio;
            let vatAmount = 0;

            if (oldPar.vatRate !== 0) {
                vatAmount = getVatAmount(
                    new Date(),
                    oldPar.billingPeriod,
                    oldPar.netPrice,
                    AuthContextService.account.country,
                    oldPar.vatRate
                );
            }

            totalPrice = ((oldPar.netPrice + vatAmount) * oldPar.quantity) / 10000.0;
        } else {
            // TEST THIS
            totalPrice = (oldPar.netPrice  * oldPar.quantity) / 10000.0;
            // throw Error('calculatePrice missing params');
        }

        return CalculatePrice.roundDigits(totalPrice, 2);
    }

    private static _getExchangeRatio(exchangeRatio: number): number {
        let modifiedExchange = exchangeRatio;
        if ([undefined, null].indexOf(exchangeRatio) >= 0) {
            modifiedExchange = 1.0;
        } else {
            modifiedExchange /= 10000.0;
        }
        return modifiedExchange;
    }
}
